import { useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { change, getFormAsyncErrors, getFormSyncErrors, submit } from 'redux-form';
import { FormAdapterContextProvider } from '../context/FormAdapterContext';
import { FORM_ADAPTER_TYPES } from '../constants';
import { waitForAsyncValidation } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const RHFFormAdapterProvider = ({
  formName,
  children
}) => {
  const dispatch = useDispatch();
  const storeInstance = useStore();
  const {
    setValue,
    watch,
    getValues,
    handleSubmit,
    reset
  } = useFormContext();
  useEffect(() => {
    /**
     * We will remove this subscription logic and push the data to the Redux store
     * once the migration is completed in the client package.
     */
    const subscription = watch((_values, {
      name
    }) => {
      if (name) {
        dispatch(change(formName, name, getValues(name)));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, formName, dispatch, getValues]);
  const handleSetValue = useCallback((name, value) => {
    setValue(name, value, {
      shouldValidate: true,
      shouldDirty: true
    });
  }, [setValue]);
  const handleGetValues = useCallback(name => name ? getValues(name) : getValues(), [getValues]);
  const handleSetValues = useCallback(values => {
    reset(currentValues => ({
      ...currentValues,
      ...values
    }), {
      keepDefaultValues: true,
      keepDirtyValues: true
    });
  }, [reset]);
  const handleTriggerValidation = useCallback(async () => {
    // Validate RHF fields
    let isRHFValid = true;
    await handleSubmit(() => {},
    // onValid, do nothing (dry run)
    () => {
      isRHFValid = false; // onInvalid, form has errors
    })();
    await waitForAsyncValidation(formName, storeInstance);
    const state = storeInstance.getState();
    const reduxFormErrors = getFormSyncErrors(formName)(state);
    const reduxFormAsyncErrors = getFormAsyncErrors(formName)(state);
    const hasNoSyncErrors = !reduxFormErrors || Object.keys(reduxFormErrors).length === 0;
    const hasNoAsyncErrors = !reduxFormAsyncErrors || Object.keys(reduxFormAsyncErrors).length === 0;
    const isReduxFormValid = hasNoSyncErrors && hasNoAsyncErrors;

    // Dispatching submit when form is invalid
    // Marks all Redux Form fields as touched → errors become visible
    if (!isReduxFormValid) {
      dispatch(submit(formName));
    }
    return isRHFValid && isReduxFormValid;
  }, [handleSubmit, dispatch, formName, storeInstance]);
  const handleSubmitForm = useCallback(async () => {
    await handleSubmit(() => dispatch(submit(formName)), () => {})();
  }, [handleSubmit, formName, dispatch]);
  const config = useMemo(() => ({
    formName,
    getValues: handleGetValues,
    setValue: handleSetValue,
    setValues: handleSetValues,
    submitForm: handleSubmitForm,
    triggerValidation: handleTriggerValidation,
    type: FORM_ADAPTER_TYPES.RHF
  }), [formName, handleSetValue, handleSetValues, handleGetValues, handleTriggerValidation, handleSubmitForm]);
  return /*#__PURE__*/_jsx(FormAdapterContextProvider, {
    value: config,
    children: children
  });
};
export default RHFFormAdapterProvider;
//# sourceMappingURL=RHFFormAdapterProvider.js.map